import { TariffPoint } from "@lib/types.ts";
import { isHalfHourlyIncrementBefore } from "../../../../../services/sales-simple-energy-comparison";
import { StateCreator } from "zustand/vanilla";
import { ComparisonTariffData } from "@stores/tariff/comparison-tariff-store.ts";

export type TariffData = {
  useTariff: boolean;
  standingCharge: number | null;
  exportRate: number | null;
  tariffPoints: TariffPoint[];
};

export const sharedTariffStateMutators = (
  set: Parameters<StateCreator<TariffData>>[0],
) => ({
  addTariff: (tariffDataToAdd: TariffData["tariffPoints"]) =>
    set((state) => {
      tariffDataToAdd.forEach((tariffToAdd) => {
        const overlappingTariff = state.tariffPoints.find((existingTariff) => {
          // We can check if dates overlap using (toAddFrom < existingTo) and (existingFrom < toAddTo)
          return (
            isHalfHourlyIncrementBefore(tariffToAdd.from, existingTariff.to) &&
            isHalfHourlyIncrementBefore(existingTariff.from, tariffToAdd.to)
          );
        });

        if (overlappingTariff) {
          throw new Error(
            `New tariff ${tariffToAdd.from} ${tariffToAdd.to} clashes with existing tariff`,
          );
        }
      });

      return {
        ...state,
        tariffPoints: [...state.tariffPoints, ...tariffDataToAdd].sort(
          (tariffA, tariffB) =>
            isHalfHourlyIncrementBefore(tariffB.from, tariffA.from) ? 1 : -1,
        ),
      };
    }),
  removeTariff: (tariffDataToRemove: TariffData["tariffPoints"]) =>
    set((state) => {
      const indexesToRemove = tariffDataToRemove.map((tariffToRemove) => {
        const matchingItem = state.tariffPoints.findIndex(
          (tariff) =>
            tariff.from === tariffToRemove.from &&
            tariff.to === tariffToRemove.to &&
            tariff.price === tariffToRemove.price,
        );

        if (matchingItem === -1) {
          throw new Error(
            `Unable to find matching tariff for ${tariffToRemove.from} ${tariffToRemove.to} ${tariffToRemove.price}`,
          );
        }

        return matchingItem;
      });

      return {
        ...state,
        tariffPoints: state.tariffPoints.filter(
          (_, idx) => !indexesToRemove.includes(idx),
        ),
      };
    }),
  setStandingCharge: (standingCharge: TariffData["standingCharge"]) =>
    set((state) => ({ ...state, standingCharge })),
  setExportRate: (exportRate: ComparisonTariffData["exportRate"]) =>
    set((state) => ({ ...state, exportRate })),
  setUseTariff: (useTariff: TariffData["useTariff"]) =>
    set((state) => ({ ...state, useTariff })),
});
